// retention-dashboard.js - Tier 2 Data & Research
export const content = {
  heroStyle: 'stats',
  tag: 'Analytics',
  stats: [
    { number: 'Real-Time', label: 'Risk Visibility', color: 'blue' },
    { number: '1 View', label: 'Complete Book Status', color: 'green' },
    { number: 'Segment', label: 'Analysis Capability', color: 'amber' },
    { number: 'Track', label: 'Progress Over Time', color: 'purple' }
  ],

  html: `
<p>You can't manage what you can't see. A retention dashboard gives you visibility into the health of your entire book—which clients need attention, how your retention is trending, where the problems are, and whether your efforts are working.</p>

<p>This guide covers what an effective retention dashboard should show you and how to use that information to make better decisions.</p>

<h2>Core Dashboard Components</h2>

<h3>1. Overall Retention Metrics</h3>

<p>The big numbers that tell you how you're doing:</p>

<ul>
<li><strong>Current retention rate:</strong> Percentage of clients retained (rolling 12 months)</li>
<li><strong>Trend direction:</strong> Improving, declining, or stable?</li>
<li><strong>Comparison to benchmark:</strong> How do you compare to industry average?</li>
<li><strong>Year-over-year change:</strong> Better or worse than last year?</li>
</ul>

<p>These headline metrics should be visible immediately when you open your dashboard.</p>

<h3>2. Risk Distribution</h3>

<p>How your book breaks down by risk level:</p>

<ul>
<li><strong>Critical risk:</strong> How many clients need immediate attention?</li>
<li><strong>High risk:</strong> How many are elevated but not urgent?</li>
<li><strong>Moderate risk:</strong> Standard monitoring population</li>
<li><strong>Low risk:</strong> Stable relationships</li>
</ul>

<p>A healthy book has most clients in low/moderate risk. Too many in critical/high indicates systemic problems.</p>

<h3>3. Priority Action List</h3>

<p>The specific clients who need attention today:</p>

<ul>
<li>Ranked by risk score (highest first)</li>
<li>Risk factors shown for each client</li>
<li>Recommended action indicated</li>
<li>Days since last contact</li>
</ul>

<p>This is your daily to-do list for retention work.</p>

<h3>4. Segment Analysis</h3>

<p>Retention broken down by meaningful categories:</p>

<ul>
<li><strong>By plan type:</strong> MA vs. Med Supp retention</li>
<li><strong>By plan:</strong> Which specific plans retain best/worst?</li>
<li><strong>By tenure:</strong> First-year vs. established clients</li>
<li><strong>By population:</strong> Dual eligibles, SNP members</li>
<li><strong>By source:</strong> AEP vs. SEP enrollees</li>
</ul>

<p>Segment analysis reveals where your retention problems actually are.</p>

<h3>5. Activity Tracking</h3>

<p>Are retention activities actually happening?</p>

<ul>
<li><strong>Touchpoint completion:</strong> How many scheduled contacts were made?</li>
<li><strong>Annual review status:</strong> What percentage completed?</li>
<li><strong>Response rates:</strong> Are clients engaging with outreach?</li>
<li><strong>Issue resolution:</strong> How quickly are problems being fixed?</li>
</ul>

<p>Activity metrics show whether your retention system is functioning.</p>

<h3>6. Alert Summary</h3>

<p>What needs attention right now:</p>

<ul>
<li><strong>New risk alerts:</strong> Clients whose risk just increased</li>
<li><strong>Plan change impacts:</strong> Clients affected by recent plan changes</li>
<li><strong>Overdue touchpoints:</strong> Scheduled contacts that weren't made</li>
<li><strong>Escalations:</strong> Situations requiring special attention</li>
</ul>

<h2>Using the Dashboard Effectively</h2>

<h3>Daily Review</h3>

<p>Every day (5-10 minutes):</p>

<ul>
<li>Check new alerts</li>
<li>Review priority action list</li>
<li>Plan today's retention calls</li>
</ul>

<h3>Weekly Review</h3>

<p>Once per week (15-20 minutes):</p>

<ul>
<li>Overall metrics trending</li>
<li>Activity completion rates</li>
<li>Risk distribution changes</li>
<li>Segment patterns</li>
</ul>

<h3>Monthly Review</h3>

<p>Once per month (30+ minutes):</p>

<ul>
<li>Deep dive on segment analysis</li>
<li>Retention rate trend analysis</li>
<li>Activity effectiveness review</li>
<li>Plan performance evaluation</li>
<li>Goal progress assessment</li>
</ul>

<h3>Pre-AEP Review</h3>

<p>August-September (thorough analysis):</p>

<ul>
<li>Risk distribution across entire book</li>
<li>High-risk client identification</li>
<li>Plan change impact assessment</li>
<li>Annual review scheduling status</li>
<li>AEP retention strategy planning</li>
</ul>

<h2>Key Performance Indicators (KPIs)</h2>

<p>Track these metrics consistently:</p>

<h3>Primary KPIs</h3>
<ul>
<li><strong>Overall retention rate:</strong> Target 85%+ (90%+ excellent)</li>
<li><strong>First-year retention:</strong> Target 85%+ (new enrollee specific)</li>
<li><strong>AEP retention:</strong> Percentage of book retained through AEP</li>
</ul>

<h3>Leading Indicators</h3>
<ul>
<li><strong>Touchpoint completion rate:</strong> Are contacts happening?</li>
<li><strong>Average contact recency:</strong> Days since average client contact</li>
<li><strong>Risk score distribution:</strong> What percentage in each tier?</li>
<li><strong>Response rate:</strong> Are clients engaging?</li>
</ul>

<h3>Lagging Indicators</h3>
<ul>
<li><strong>Churn rate:</strong> Actual clients lost</li>
<li><strong>Churn reasons:</strong> Why they left</li>
<li><strong>Commission impact:</strong> Revenue lost to churn</li>
</ul>

<p>Leading indicators predict future retention. Lagging indicators confirm what happened.</p>

<h2>Dashboard Design Principles</h2>

<h3>Actionable First</h3>
<p>The most important information should answer: "What do I need to do today?" Priority lists and alerts should be prominent.</p>

<h3>Trends Over Snapshots</h3>
<p>A single number is less useful than a trend. Is 82% retention good? Depends—better than last month? Worse than last year? Show direction.</p>

<h3>Drill-Down Capability</h3>
<p>Start with summary, click to details. See overall retention, then drill into segments, then into individual clients.</p>

<h3>Comparison Context</h3>
<p>Numbers need context. Show benchmarks, targets, and historical comparisons so you know whether metrics are good or bad.</p>

<h2>Building Your Dashboard</h2>

<h3>If Using Pro IRP</h3>
<p>Dashboard is built-in with all components described above. Customize views to your preferences.</p>

<h3>If Building Custom</h3>
<p>Start with:</p>
<ul>
<li>Spreadsheet or basic BI tool</li>
<li>Manual data updates (weekly minimum)</li>
<li>Focus on: retention rate, risk distribution, priority list</li>
<li>Add complexity as needed</li>
</ul>

<p>Something is better than nothing. A basic dashboard beats no visibility.</p>
  `,

  faqs: [
    {
      question: "What are the most important metrics for a retention dashboard?",
      answer: "Primary KPIs: overall retention rate, first-year retention, AEP retention. Leading indicators: touchpoint completion, contact recency, risk distribution. These tell you both how you're doing and predict where you're heading."
    },
    {
      question: "How often should I review my retention dashboard?",
      answer: "Daily: check alerts and priority list (5-10 min). Weekly: overall trends and activity rates (15-20 min). Monthly: deep dive on segments and effectiveness (30+ min). Pre-AEP: thorough analysis and planning."
    },
    {
      question: "What should the priority action list show?",
      answer: "Clients ranked by risk score (highest first), risk factors for each client, recommended action, and days since last contact. This is your daily retention to-do list—work from top to bottom."
    },
    {
      question: "How do I segment retention analysis?",
      answer: "Break down by: plan type (MA vs. Med Supp), specific plan, tenure (first-year vs. established), population (dual eligibles, SNP), and enrollment source (AEP vs. SEP). Segment analysis reveals where problems actually are."
    },
    {
      question: "What's the difference between leading and lagging indicators?",
      answer: "Leading indicators predict future retention: touchpoint completion, contact recency, risk distribution, response rates. Lagging indicators confirm what happened: churn rate, churn reasons, commission impact. Watch leading indicators to change outcomes before they happen."
    }
  ],

  sources: [
    {
      title: "Medicare Retention Analytics",
      url: "https://www.cms.gov/Research-Statistics-Data-and-Systems",
      publisher: "CMS",
      accessedDate: "2024-12-10"
    }
  ],

  ctaDescription: `Pro IRP's retention dashboard gives you complete visibility into your book: real-time risk scores, segment analysis, activity tracking, and actionable priority lists. Know exactly where you stand and what needs attention.`
};

export default content;
